import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Clock, Car, Phone, Navigation, AlertCircle } from 'lucide-react';
import { GoogleMap, LoadScript, Marker, Polyline } from '@react-google-maps/api';
import { decode } from '@googlemaps/polyline-codec';
import { getTrackingData } from '../utils/api';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const POLL_INTERVAL = 10000; // 10 seconds

const TrackRide = () => {
  const { bookingId } = useParams();
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [map, setMap] = useState(null);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  const fetchTracking = useCallback(async () => {
    try {
      const data = await getTrackingData(bookingId);
      setTrackingData(data);
      setError(null);
      
      // Fit bounds when map is loaded
      if (map && data) {
        const bounds = new window.google.maps.LatLngBounds();
        
        if (data.driverLatitude && data.driverLongitude) {
          bounds.extend({ lat: data.driverLatitude, lng: data.driverLongitude });
        }
        if (data.pickupLatitude && data.pickupLongitude) {
          bounds.extend({ lat: data.pickupLatitude, lng: data.pickupLongitude });
        }
        if (data.dropoffLatitude && data.dropoffLongitude) {
          bounds.extend({ lat: data.dropoffLatitude, lng: data.dropoffLongitude });
        }
        
        if (!bounds.isEmpty()) {
          map.fitBounds(bounds);
        }
      }
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      setError('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, [bookingId, map]);

  useEffect(() => {
    fetchTracking();
    
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchTracking, POLL_INTERVAL);
    
    return () => clearInterval(interval);
  }, [fetchTracking]);

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    setMapsLoaded(true);
  }, []);

  // Decode polyline if available
  const routePath = trackingData?.routePolyline
    ? decode(trackingData.routePolyline, 5).map(([lat, lng]) => ({ lat, lng }))
    : [];

  // Create marker icons only after Google Maps is loaded
  const markerIcons = useMemo(() => {
    if (!mapsLoaded || !window.google) return null;
    
    return {
      driver: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="18" fill="#EAB308" stroke="white" stroke-width="2"/>
            <path d="M14 20h12M20 14v12" stroke="white" stroke-width="2" stroke-linecap="round"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(40, 40)
      },
      pickup: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="#06B6D4" stroke="white" stroke-width="2"/>
            <text x="16" y="22" font-family="Arial" font-size="16" font-weight="bold" fill="white" text-anchor="middle">P</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32)
      },
      dropoff: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="#EF4444" stroke="white" stroke-width="2"/>
            <text x="16" y="22" font-family="Arial" font-size="16" font-weight="bold" fill="white" text-anchor="middle">D</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32)
      }
    };
  }, [mapsLoaded]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-32 pb-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading tracking data...</p>
        </div>
      </div>
    );
  }

  if (error || !trackingData) {
    return (
      <div className="min-h-screen bg-black pt-32 pb-20 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">Tracking Unavailable</h2>
          <p className="text-gray-400 mb-2">
            {error || 'We couldn\'t find tracking data for this booking.'}
          </p>
          <p className="text-sm text-gray-500">Booking ID: {bookingId}</p>
        </div>
      </div>
    );
  }

  const mapCenter = trackingData.driverLatitude && trackingData.driverLongitude
    ? { lat: trackingData.driverLatitude, lng: trackingData.driverLongitude }
    : trackingData.pickupLatitude && trackingData.pickupLongitude
    ? { lat: trackingData.pickupLatitude, lng: trackingData.pickupLongitude }
    : { lat: 25.0343, lng: -77.3963 }; // Nassau default

  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="bg-yellow-500/10 text-yellow-400 px-4 py-2 rounded-full text-sm font-semibold border border-yellow-500/30 inline-block mb-4">
            {trackingData.trackingEnabled ? 'Live Tracking' : 'Tracking Info'}
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Track Your Ride
          </h1>
          <p className="text-xl text-gray-400">Booking ID: {trackingData.id}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Map */}
          <div className="lg:col-span-2">
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-yellow-500/20 overflow-hidden h-96 lg:h-[600px]">
              {GOOGLE_MAPS_KEY ? (
                <LoadScript googleMapsApiKey={GOOGLE_MAPS_KEY}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={13}
                    onLoad={onMapLoad}
                    options={{
                      zoomControl: true,
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      styles: [
                        {
                          featureType: 'all',
                          elementType: 'geometry',
                          stylers: [{ color: '#242f3e' }]
                        },
                        {
                          featureType: 'all',
                          elementType: 'labels.text.stroke',
                          stylers: [{ color: '#242f3e' }]
                        },
                        {
                          featureType: 'all',
                          elementType: 'labels.text.fill',
                          stylers: [{ color: '#746855' }]
                        }
                      ]
                    }}
                  >
                    {/* Driver Marker */}
                    {trackingData.driverLatitude && trackingData.driverLongitude && markerIcons && (
                      <Marker
                        position={{ lat: trackingData.driverLatitude, lng: trackingData.driverLongitude }}
                        icon={markerIcons.driver}
                        title="Driver Location"
                      />
                    )}

                    {/* Pickup Marker */}
                    {trackingData.pickupLatitude && trackingData.pickupLongitude && markerIcons && (
                      <Marker
                        position={{ lat: trackingData.pickupLatitude, lng: trackingData.pickupLongitude }}
                        icon={markerIcons.pickup}
                        title="Pickup Location"
                      />
                    )}

                    {/* Dropoff Marker */}
                    {trackingData.dropoffLatitude && trackingData.dropoffLongitude && markerIcons && (
                      <Marker
                        position={{ lat: trackingData.dropoffLatitude, lng: trackingData.dropoffLongitude }}
                        icon={markerIcons.dropoff}
                        title="Dropoff Location"
                      />
                    )}

                    {/* Route Polyline */}
                    {routePath.length > 0 && (
                      <Polyline
                        path={routePath}
                        options={{
                          strokeColor: '#EAB308',
                          strokeOpacity: 0.8,
                          strokeWeight: 4
                        }}
                      />
                    )}
                  </GoogleMap>
                </LoadScript>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-400">
                    <MapPin className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Map unavailable</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Trip Status */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-yellow-500/20 p-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                <Navigation className="w-5 h-5 mr-2 text-yellow-500" />
                Trip Status
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-yellow-400 font-semibold">{trackingData.status}</span>
                </div>
                {trackingData.liveEtaText && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">ETA:</span>
                    <span className="text-white font-bold flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-yellow-500" />
                      {trackingData.liveEtaText}
                    </span>
                  </div>
                )}
                {trackingData.liveDistanceText && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Distance:</span>
                    <span className="text-white font-semibold">{trackingData.liveDistanceText}</span>
                  </div>
                )}
                {trackingData.driverLastLocationUpdate && (
                  <div className="mt-3 pt-3 border-t border-gray-700">
                    <p className="text-xs text-gray-500">
                      Last updated: {new Date(trackingData.driverLastLocationUpdate).toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Driver Info */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-yellow-500/20 p-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                <Car className="w-5 h-5 mr-2 text-yellow-500" />
                Driver Information
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm">Driver</p>
                  <p className="text-white font-semibold">{trackingData.driverName}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Vehicle</p>
                  <p className="text-white font-semibold">{trackingData.driverVehicleInfo}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">License Plate</p>
                  <p className="text-white font-semibold">{trackingData.driverLicensePlate}</p>
                </div>
                <a
                  href={`tel:${trackingData.driverPhone}`}
                  className="mt-4 w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-3 rounded-lg font-bold hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 flex items-center justify-center"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Driver
                </a>
              </div>
            </div>

            {/* Trip Details */}
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl border border-yellow-500/20 p-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-yellow-500" />
                Trip Details
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-gray-400 text-sm">Pickup</p>
                  <p className="text-white font-semibold">{trackingData.pickupLocation}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Dropoff</p>
                  <p className="text-white font-semibold">{trackingData.dropoffLocation}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-700">
                  <div>
                    <p className="text-gray-400 text-sm">Date</p>
                    <p className="text-white font-semibold">{trackingData.date}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Time</p>
                    <p className="text-white font-semibold">{trackingData.time}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackRide;
